// The information contained in this document are the sole property of LivingPackets. Any disclosure to any third party and any reproduction, in part or whole without the written permission of LivingPackets is prohibited
// Confidential - Copyright LivingPackets: All rights reserved

import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import { PartnerStatsEntity } from '../entities/PartnerStatsEntity';
import useImperativeRequestWrapper from '@/hooks/useImperativeRequestWrapper';
import { ExtractFnReturnType, QueryConfig } from '@/lib/react-query';

export const getPartnerStats = async ({
  partnerId,
  makeRequestLPVillage,
  makeRequestShipment,
  makeRequestDevice,
}: {
  partnerId: string;
  makeRequestLPVillage: any;
  makeRequestShipment: any;
  makeRequestDevice: any;
}): Promise<PartnerStatsEntity> => {
  try {
    // BOXs Under Contract
    const { data: dataUnderContract, error: errorUnderContractResponse } =
      await makeRequestLPVillage({
        path: `/partners/${partnerId}/sylob/nb-contractual-boxes`,
      });

    const underContractValue = errorUnderContractResponse
      ? NaN
      : parseInt(dataUnderContract?.count, 10);

    // UnpaidBills
    const { data: dataUnpaidBills, error: errorUnpaidBillsResponse } =
      await makeRequestLPVillage({
        path: `/partners/${partnerId}/sylob/has-unpaid-bills`,
      });

    const is403Response = errorUnpaidBillsResponse?.status === 403;

    const unpaidBillsValue = is403Response
      ? 0
      : dataUnpaidBills?.has_unpaid_bills
        ? 1
        : 0;

    const unpaidBillsError = is403Response ? null : errorUnpaidBillsResponse;

    // Litigation
    const { data: dataLitigations, error: errorLitigationsResponse } =
      await makeRequestShipment({
        path: `/partners/${partnerId}/litigations/count?statuses=FLAGGED`,
      });

    // InBusiness / ToIsolate
    const { data: dataLifecycle, error: errorLifecycleResponse } =
      await makeRequestDevice({
        path: `/partners/${partnerId}/lifecycles/count`,
      });

    // Check if all requests failed
    if (
      errorUnpaidBillsResponse &&
      errorLitigationsResponse &&
      errorLifecycleResponse
    ) {
      throw new Error(`Failed to fetch all partner stats`);
    }

    return PartnerStatsEntity.createFromAPI({
      unpaid_bills: {
        value: unpaidBillsValue,
        error: unpaidBillsError,
      },
      litigation: {
        value: dataLitigations?.count ?? null,
        error: errorLitigationsResponse || null,
      },
      in_business: {
        value: dataLifecycle?.in_business ?? null,
        error: errorLifecycleResponse || null,
      },
      under_contract: {
        value: underContractValue,
        error: errorUnderContractResponse || null,
      },
      to_isolate: {
        value: dataLifecycle?.to_isolate ?? null,
        error: errorLifecycleResponse || null,
      },
    });
  } catch (error) {
    throw new Error('Failed to fetch all partner stats');
  }
};

type QueryFnType = typeof getPartnerStats;

type UsePartnerStatsOptions = {
  config?: QueryConfig<QueryFnType>;
  partnerId?: string;
};

export const usePartnerStats = ({
  config,
  partnerId,
}: UsePartnerStatsOptions) => {
  const { id: partnerIdParams } = useParams() as { id: string };

  // lpVillageV3: '/v3/lpvillage',
  const [, makeRequestLPVillage] = useImperativeRequestWrapper('lpVillageV3');
  // shipment: '/shipment/api/v2',
  const [, makeRequestShipment] = useImperativeRequestWrapper('shipmentV3');
  // deviceV3: '/v3/device',
  const [, makeRequestDevice] = useImperativeRequestWrapper('deviceV3');

  return useQuery<ExtractFnReturnType<QueryFnType>>({
    ...config,
    queryKey: ['partnership', partnerIdParams, 'stats'],
    queryFn: () =>
      getPartnerStats({
        partnerId: partnerId ? partnerId : partnerIdParams,
        makeRequestLPVillage,
        makeRequestShipment,
        makeRequestDevice,
      }),
  });
};
